import type { Site } from '@/entities/sites/api/types';
import type { SortDirection } from '@/shared/api/types';
import type { ContentStatus } from '@/shared/lib/constants/contentStatuses';
import type { PlayerFormat } from '@/shared/types';
import type { PerPage } from '@/shared/ui/pagination/lib/config';
import type {
  Slide,
  StylesConfig as PlayerStylesConfig,
} from '@player/content';

// App-side StylesConfig uses `file_path` for API, player uses `src` for full URL
export type StylesConfig = Omit<PlayerStylesConfig, 'logo'> & {
  logo: Omit<PlayerStylesConfig['logo'], 'src'> & {
    file_path: string;
  };
};

/*
 * Ad Units models
 */
export type Zone = {
  id: number;
  name: string;
};

export enum TextPosition {
  Bottom = 'bottom',
  Center = 'center',
}

export type ContentStyles = {
  id: number;
  type: 'video' | 'slider';
  name: string;
  config: StylesConfig;
};

export interface ContentResponseDto<T> {
  data: T;
}

export type ModifyContentStylesDto = Omit<ContentStyles, 'id'>;

export type ModifyContentDto = Omit<
  Content,
  | 'id'
  | 'duration'
  | 'updated_at'
  | 'created_at'
  | 'dislikes_count'
  | 'likes_count'
  | 'views_count'
> & {
  video: Blob;
};

/*
 * Content models
 */

export type TextBlock = {
  // Start position in seconds, non-negative number,
  // cannot be greater than the `end` value.
  start: number;
  // End position in seconds, positive number,
  // cannot be less than the `start` value.
  end: number;
  // Text caption of the link, minimum 3 characters.
  content: string;
  // URL content of the link, must be valid URL.
  url?: string;
};

export enum ContentPublication {
  NotSpecified = 0,
  SpecifiedPages = 1,
  ExcludingPages = 2,
}

export enum RssUpdatePeriod {
  Every24Hours = 1,
  Every48Hours = 2,
  Every7Days = 7,
}

export type RssSettings = {
  link: string;
  limit: number;
  auto_update: {
    enabled: boolean;
    time?: string; // HH:MM
    interval_days?: number; // 1,2,7
  };
};

export type Content = {
  id: number;
  name: string;
  type: 'video' | 'slider';
  status: ContentStatus;
  format: PlayerFormat;
  config: StylesConfig;

  // publication:
  sites: Site['id'][];
  zones: Zone['id'][];
  categories: number[];
  publication: ContentPublication;
  publication_urls: string;

  // video:
  editor_config?: string;
  text_blocks?: TextBlock[];
  duration?: number;
  media?: {
    hls_path: string;
  };

  // slider:
  rss_settings?: RssSettings;
  slides?: Slide[];

  // unused:
  likes_count: number;
  dislikes_count: number;
  views_count: number;

  // service:
  created_at: string;
  updated_at: string;
};

export type ContentSortKey = keyof Pick<
  Content,
  'likes_count' | 'views_count' | 'updated_at'
>;

export interface ContentArg {
  dateTo: string;
  dateFrom: string;
  sites: Site['id'][];
  zones: Zone['id'][];
  page?: number;
  per_page?: PerPage;
  sort?: ContentSortKey;
  direction?: SortDirection;
  format: PlayerFormat[];
  contentStatuses: ContentStatus[];
  search?: string;
}
